/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { createSystem, defaultConfig, defineConfig } from 'chakra-v3';
import { caseStudyCardRecipe } from './recipes/CaseStudyCard.recipe';
import { radioGroupRecipe } from './recipes/RadioGroup.recipe';
import { EvalsKaryaColors } from './Colors';

/**
 * Minimalist Chakra UI v3 Theme Configuration
 *
 * Philosophy: Keep it simple, only define what you need.
 *
 * Usage in components:
 * - Use semantic tokens: bg="bg.primary", color="text.primary"
 * - Or direct tokens: color="brand.teal", bg="brand.blue"
 * - Button variants: <Button variant="cta">Click me</Button>
 * - Text variants: <Text variant="body">Body text</Text>
 *
 * Note: Button and Text components are created directly from recipes using chakra()
 * factory, so their recipes are not registered here. Only slotRecipes that use
 * useSlotRecipe hook need to be registered in the theme.
 */
export const theme = createSystem(
  defaultConfig,
  defineConfig({
    preflight: true,
    globalCss: {
      // Note: preflight already handles * { margin: 0; padding: 0; box-sizing: border-box; }
      // Only add custom global styles here
      html: {
        // Scrollbar styling on html ensures consistency when html element itself scrolls
        scrollbarWidth: 'thin', // Firefox - thin scrollbar
        scrollbarColor: 'rgba(0, 0, 0, 0.4) rgba(0, 0, 0, 0.05)', // Firefox - visible thumb and subtle track
        scrollBehavior: 'smooth',
        // @ts-expect-error - vendor prefix property for IE/Edge
        '-ms-overflow-style': 'auto', // IE and Edge - show scrollbar
      },
      body: {
        margin: 0,
        padding: 0,
        scrollBehavior: 'smooth',
        overflowX: 'hidden', // Prevent horizontal scrolling
        overflowY: 'auto', // Enable vertical scrolling with visible scrollbar
        fontFamily: "'DM Sans', sans-serif",
        // Scrollbar styling on body for main content area - thin but visible for accessibility
        scrollbarWidth: 'thin', // Firefox - thin scrollbar
        scrollbarColor: 'rgba(0, 0, 0, 0.4) rgba(0, 0, 0, 0.05)', // Firefox - visible thumb and subtle track
        // @ts-expect-error - vendor prefix property for IE/Edge
        '-ms-overflow-style': 'auto', // IE and Edge - show scrollbar
      },
      // Webkit scrollbar styling (Chrome, Safari, Opera) - applied to both html and body for consistency
      'html::-webkit-scrollbar, body::-webkit-scrollbar': {
        width: '6px', // Thin but visible scrollbar width
      },

      'html::-webkit-scrollbar-thumb:hover, body::-webkit-scrollbar-thumb:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.6)', // Darker on hover for better visibility and interaction feedback
        boxShadow: 'none', // Remove any default shadows
      },
      // Heading styles from theme
      h2: {
        fontSize: '20px',
        fontWeight: '600',
        lineHeight: '1.4',
        color: EvalsKaryaColors.white, // text.primary color
      },
    },
    theme: {
      breakpoints: {
        sm: '30em', // 480px (mobile)
        md: '48em', // 768px (tablet)
        lg: '64em', // 1024px (laptop/desktop)
        xl: '80em', // 1280px
        '2xl': '96em', // 1536px
      },
      slotRecipes: {
        caseStudyCard: caseStudyCardRecipe,
        radioGroup: radioGroupRecipe,
      },
      // Raw Color Tokens - Your minimalist palette
      tokens: {
        colors: {
          brand: {
            teal: { value: EvalsKaryaColors.teal },
            tealLight: { value: EvalsKaryaColors.tealLight },
            tealDark: { value: EvalsKaryaColors.tealDark },
            tealGradient1: { value: EvalsKaryaColors.tealGradient1 },
            white: { value: EvalsKaryaColors.white },
            white10: { value: EvalsKaryaColors.white10 },
            white20: { value: EvalsKaryaColors.white20 },
            heroText: { value: EvalsKaryaColors.heroText },
            blue: { value: EvalsKaryaColors.secondaryBlue },
            secondaryBlue2: { value: EvalsKaryaColors.secondaryBlue2 },
            lighterBlue: { value: EvalsKaryaColors.lighterBlue },
            cta: { value: EvalsKaryaColors.buttonCta },
            ctaHover: { value: EvalsKaryaColors.buttonCtaHover },
            beige: { value: EvalsKaryaColors.beige },
            lightBeige: { value: EvalsKaryaColors.lightBeige },
            grey: { value: EvalsKaryaColors.grey },
            grey10: { value: EvalsKaryaColors.grey10 },
            grey20: { value: EvalsKaryaColors.grey20 },
            errorBg: { value: EvalsKaryaColors.errorBg },
            errorBorder: { value: EvalsKaryaColors.errorBorder },
            errorText: { value: EvalsKaryaColors.errorText },
            errorSolid: { value: EvalsKaryaColors.errorSolid },
          },
        },
        fonts: {
          // cspell:ignore Noto Jakarta
          heading: { value: "'Noto Serif', serif" },
          body: { value: "'DM Sans', sans-serif" },
          subheading: { value: "'Plus Jakarta Sans', sans-serif" },
        },
      },
      // Semantic Tokens - Give your colors meaning
      semanticTokens: {
        colors: {
          // Background colors
          bg: {
            primary: { value: '{colors.brand.teal}' },
            light: { value: '{colors.brand.white}' },
            surface: { value: '{colors.brand.beige}' },
            card: { value: '{colors.brand.teal}' },
            inputGradient: {
              value:
                'radial-gradient(99.74% 65.32% at 48.71% 100%, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.00) 100%)',
            },
          },
          // Text colors
          text: {
            primary: { value: '{colors.brand.white}' },
            teal: { value: '{colors.brand.teal}' },
            darkTeal: { value: '{colors.brand.tealDark}' },
            dark: { value: '{colors.brand.teal}' },
            hero: { value: '{colors.brand.heroText}' },
            accent: { value: '{colors.brand.blue}' },
            footer: { value: '{colors.brand.beige}' },
            grey20: { value: '{colors.brand.grey20}' },
            label: { value: '{colors.brand.beige}' },
            placeholder: { value: 'rgba(176, 188, 192, 1)' },
            helper: { value: '{colors.brand.grey10}' },
            blue: { value: '{colors.brand.secondaryBlue}' },
            secondaryBlue: { value: '{colors.brand.secondaryBlue2}' },
          },
          // Button/CTA colors
          cta: {
            solid: { value: '{colors.brand.cta}' },
            hover: { value: '{colors.brand.ctaHover}' },
          },
          // Border colors
          border: {
            darkStroke: {
              value:
                'radial-gradient(293.86% 142.07% at 1.02% 0%, {colors.brand.teal} 0%, {colors.brand.tealLight} 100%)',
            },
            whiteGradient: {
              value: 'linear-gradient(0deg, rgba(246, 246, 246, 0.30) 0%, rgba(255, 255, 255, 0.09) 100%)',
            },
            teal: {
              value: 'color-mix(in srgb, #0A313C 90%, white 10%)',
            },
            tealGradient1: { value: '{colors.brand.tealGradient1}' },
            white20: {
              value: 'rgba(255, 255, 255, 0.20)',
            },
          },
          separator: {
            tealGradient1: { value: '{colors.brand.tealGradient1}' },
          },
          // Background shadow/glow effects
          backgroundShadowBlue: {
            value: 'radial-gradient(293.86% 142.07% at 1.02% 0%, rgba(3, 94, 119, 0.50) 0%, #055369 100%)',
          },
          // Subtle blur effect with border for CardShell
          blurShadowBlue: {
            value: 'radial-gradient(293.86% 142.07% at 1.02% 0%, rgba(47, 82, 92, 0.50) 0%, #055369 100%)',
          },
          // Glow effects for buttons and interactive elements
          glow: {
            textShadow: {
              value: '0 0 12px rgba(255, 255, 255, 0.4), 0 0 24px rgba(255, 255, 255, 0.2)',
            },
            iconFilter: {
              value: 'drop-shadow(0 0 12px rgba(255, 255, 255, 0.4)) drop-shadow(0 0 24px rgba(255, 255, 255, 0.2))',
            },
          },
          // Stepper component colors
          stepper: {
            background: {
              value: 'radial-gradient(293.86% 142.07% at 1.02% 0%, rgba(47, 82, 92, 0.50) 0%, #055369 100%)',
            },
          },
          // Connector colors
          connector: {
            default: { value: 'rgba(47, 82, 92, 0.50)' },
          },
          spinner: {
            blue: { value: '{colors.brand.blue}' },
            secondaryBlue: { value: '{colors.brand.secondaryBlue}' },
          },
          // Tag colors
          tag: {
            background: { value: '{colors.brand.lighterBlue}' },
            text: { value: '{colors.brand.tealDark}' },
          },
          // Select component colors
          select: {
            hover: { value: 'color-mix(in srgb, {colors.brand.secondaryBlue} 10%, transparent)' }, // secondaryBlue with 0.1 opacity
            separator: { value: '{colors.brand.cta}' }, // matches buttonCta color
          },
          // Error colors
          error: {
            bg: { value: '{colors.brand.errorBg}' },
            border: { value: '{colors.brand.errorBorder}' },
            text: { value: '{colors.brand.errorText}' },
            solid: { value: '{colors.brand.errorSolid}' },
          },
          svg: {
            secondaryBlue: { value: '{colors.brand.secondaryBlue}' },
            secondaryBlue2: { value: '{colors.brand.secondaryBlue2}' },
          },
        },
      },
    },
  }),
);

export default theme;

export { EvalsKaryaColors };
